<script lang="ts">
  import { onMount, untrack } from "svelte"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import PostalRecipientForm, {
    postalRecipientValidation,
  } from "../../../../../../../../../../../../_shared/_modules/postal-recipient-form"
  import ActionBox from "@kontextwerk/web-sdk/ui/layout/ActionBox.svelte"
  import type { InboxWorkflowRowEditorProps } from "@kontextwerk/control-web-frame/inbox-workflow/contract"
  import type { LetterSelectionDraft, LetterSelectionRow } from "../../contract"
  import {
    manualRecipientValue,
    updateAddressField,
  } from "../../../../_shared/_logic/print-selection"
  import { recipientEditorContext, recipientEditorReason } from "../../_shared/_logic/recipient"

  let {
    row,
    draft,
    disabled,
    readOnly,
    save,
    onshowOverlay,
    setActionButtonSnippet,
  }: InboxWorkflowRowEditorProps<LetterSelectionRow, LetterSelectionDraft> = $props()
  const initialContext = untrack(() => recipientEditorContext(row, draft))
  let value = $state(
    untrack(() => ({
      recipientName: draft.recipientName ?? "",
      country: draft.recipientAddress?.country ?? "",
      street: draft.recipientAddress?.street ?? "",
      house_number: draft.recipientAddress?.house_number ?? "",
      zip: draft.recipientAddress?.zip ?? "",
      city: draft.recipientAddress?.city ?? "",
      address_extra: draft.recipientAddress?.address_extra ?? "",
    })),
  )
  const fields = [
    { key: "recipientName", label: "Name" },
    { key: "country", label: "Land" },
    { key: "street", label: "Straße" },
    { key: "house_number", label: "Hausnummer" },
    { key: "zip", label: "PLZ" },
    { key: "city", label: "Ort" },
    { key: "address_extra", label: "Adresszusatz" },
  ] as const
  const id = $props.id()
  const manual = $derived(draft.recipientValue === manualRecipientValue)
  const contextChanged = $derived(initialContext !== recipientEditorContext(row, draft))
  const blocked = $derived(
    disabled || readOnly || contextChanged || Boolean(recipientEditorReason(row, draft)),
  )
  let error = $state<string | null>(null)

  const saveDraft = () => {
    if (blocked) return

    error = postalRecipientValidation(value)

    if (error) return

    const patch: Partial<LetterSelectionDraft> = { recipientName: value.recipientName.trim() }

    if (manual) {
      let address = draft.recipientAddress
      for (const field of fields) {
        if (field.key !== "recipientName")
          address = updateAddressField(address, field.key, value[field.key].trim())
      }
      patch.recipientAddress = address
    }

    error = save(patch)
  }

  onMount(() => {
    const registerActions = setActionButtonSnippet
    registerActions?.(footerSnippet)
    onshowOverlay?.()

    return () => registerActions?.(null)
  })
</script>

{#snippet footerSnippet()}
  <div class="editor-actions">
    <CTA
      type="primaryButton"
      content="Speichern"
      darkMode={false}
      disabled={blocked}
      onclick={saveDraft}
      tooltipContent="Speichert den Entwurf in dieser Zeile. Übernommen wird er anschließend mit Briefauswahl bestätigen."
    />
  </div>
{/snippet}

<div class="editor-content">
  <section class="address-form" aria-label="Zieladresse">
    <ActionBox label="Zieladresse" darkMode={false}>
      <PostalRecipientForm bind:value {manual} disabled={blocked} />
    </ActionBox>
  </section>
  {#if contextChanged}<p role="alert" class="editor-error"
      >Der Empfänger wurde inzwischen geändert. Öffnen Sie die Bearbeitung erneut.</p
    >
  {:else if error}<p role="alert" class="editor-error">{error}</p>{/if}
</div>

<style>
  .editor-content {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    width: 100%;
    min-width: 0;
    gap: 1rem;
    color: var(--text-invers-100);
  }
  .address-form {
    width: 100%;
    min-width: 0;
    container-type: inline-size;
  }
  .address-form :global(.box) {
    width: 100%;
    min-width: 0;
    height: auto;
    box-sizing: border-box;
    border-color: color-mix(in srgb, var(--text-invers-100) 25%, transparent);
  }
  .editor-actions {
    display: flex;
    justify-content: flex-end;
    width: 100%;
    min-width: 0;
  }
  .editor-error {
    margin: 0;
    color: var(--error-100, #b42318);
  }
</style>
